/**
 * One fluid gutter shared by the new home page, so its sections can't drift
 * apart and none of them stops growing partway across a wide screen.
 *
 * There is deliberately no max-width. A capped container (the old
 * `max-w-7xl` / `max-w-[1340px]`) stops growing at 1280-1340px, so every pixel
 * of screen past that becomes an empty band down both sides — the wider the
 * display, the more dead space. Instead the gutter itself is a percentage from
 * `xl` up, so content always spans ~95% of the viewport, at 1280 and at 2560
 * alike.
 *
 * 2.5% is chosen to be continuous with the step below it: at exactly 1280px it
 * resolves to 32px, the same as `lg:px-8`, so nothing shifts at the breakpoint.
 * Above that it just keeps pace — 48px at 1920, 64px at 2560.
 *
 * Percentages, not vw: a percentage resolves against the parent's content box,
 * which excludes the scrollbar. 100vw doesn't, and would push everything
 * ~15px off-centre in a desktop browser with classic scrollbars.
 */
export const PAGE_GUTTER = "px-4 sm:px-6 lg:px-8 xl:px-[120px]" as const;

/**
 * The carousels are full-bleed so their tracks can run off the right edge, but
 * the left edge still has to line up with PAGE_GUTTER above. Same ramp, left
 * side only. From 1700px it picks up the Banner copy's extra 50px indent
 * (120 + 50) so every section starts on the hero's text edge.
 */
export const EDGE_LEFT =
  "pl-4 sm:pl-6 lg:pl-8 xl:pl-[120px] min-[1700px]:pl-[170px]" as const;

export const EDGE_RIGHT = "pr-4 sm:pr-6 lg:pr-8 xl:pr-[120px]" as const;

/**
 * The tail counterpart to EDGE_LEFT for those carousels: an empty flex item
 * after the last card, so once the track is scrolled to its end the last card
 * stops the same distance from the right edge as the first one starts from the
 * left. Desktop only — on mobile the track is still meant to bleed off the
 * right edge, so the spacer has no width below `md`.
 *
 * It's a pseudo-element and not `pr-*` on the track itself because trailing
 * padding on a horizontal scroll container is honoured inconsistently across
 * browsers; the widths mirror EDGE_LEFT at each step (24 / 32 / 120px).
 *
 * Two variants, because the tracks carry their card gap differently:
 * - EDGE_TAIL where the gap is a right *margin* on the card. Pair it with
 *   `md:last:mr-0` so the trailing margin doesn't stack on top of the spacer.
 * - EDGE_TAIL_INSET where the gap is padding *inside* the card's own width
 *   (PartnershipMncs). There the last 14px of the track is already empty, so
 *   the spacer is 14px narrower to land on the same gutter.
 */
export const EDGE_TAIL =
  "after:shrink-0 after:content-[''] md:after:w-6 lg:after:w-8 xl:after:w-[120px]" as const;

export const EDGE_TAIL_INSET =
  "after:shrink-0 after:content-[''] md:after:w-[10px] lg:after:w-[18px] xl:after:w-[106px]" as const;
